import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { GradeItemType } from 'src_common/common/websocket2/modelsApi/Event';
import { SdkStaff } from 'src/AppState/sdk/staff';
import { MobxMap } from 'src_common/common/mobx-utils/MobxMap';
import { EventId } from 'src_common/common/websocket2/id/WebsocketId';
import { GradeType } from 'src/domains/sportsbook/trading/sdk/models/grades/gradeModels';

interface PostBodyTypes {
    grades?: {
        prematch?: { id: number };
        inPlay?: { id: number };
        show?: { id: number };
    };
}

export class GradeState {
    @observable isLoadingEventUpdate: boolean = false;
    private readonly gradesDataMap: MobxMap<[string], Array<GradeType>>;

    public constructor(
        private readonly eventIdInner: () => EventId,
        private readonly sdkStaff: SdkStaff
    ) {
        this.gradesDataMap = new MobxMap(async ([sport]: [string]): Promise<Array<GradeType>> => {
            return await sdkStaff.trading.getSportsGrades(sport);
        });
        makeAutoObservable(this);
    }

    @computed get eventId(): EventId {
        return this.eventIdInner();
    }

    @computed get eventModel(): EventModel | null {
        return this.eventId.getEventModel();
    }

    @computed get gradesData(): Array<GradeType> {
        if (this.eventModel !== null) {
            const resource = this.gradesDataMap.get([this.eventModel.sport]).get();

            if (resource.type === 'ready') {
                return resource.value;
            }
        }
        return [];
    }

    @computed get gradesForView(): Array<{ id?: number | null; name: string }> {
        return this.gradesData.map((elem) => {
            return {
                id: elem.id,
                name: elem.displayName ? elem.displayName : elem.name,
            };
        });
    }

    @computed get gradesPrematch(): GradeItemType | null {
        return this.eventModel?.gradesPrematch ?? null;
    }

    @computed get gradesInPlay(): GradeItemType | null {
        return this.eventModel?.gradesInPlay ?? null;
    }

    @computed get gradesShow(): GradeItemType | null {
        return this.eventModel?.gradesShow ?? null;
    }

    @action onChangeGradesPrematch = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        const prematch = e.currentTarget.value;

        await this.performUpdate({
            grades: {
                prematch: { id: parseInt(prematch, 10) ?? 0 },
            },
        });
    };

    @action onChangeGradesInPlay = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        const inPlay = e.currentTarget.value;

        await this.performUpdate({
            grades: {
                inPlay: { id: parseInt(inPlay, 10) ?? 0 },
            },
        });
    };

    @action onChangeGradesShow = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
        const show = e.currentTarget.value;

        await this.performUpdate({
            grades: {
                show: { id: parseInt(show, 10) ?? 0 },
            },
        });
    };

    @action performUpdate = async (body: PostBodyTypes): Promise<void> => {
        this.isLoadingEventUpdate = true;
        await this.sdkStaff.updateEvent({ event_id: this.eventId.toOldId(), requestBody: body });
        this.isLoadingEventUpdate = false;
    };
}
